// src/VerifyBid.js
// Página de verificación de oferta desde email
// Componente ligero que solo renderiza - lógica delegada a useBidVerification

import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { CheckCircle, XCircle, Clock, AlertTriangle } from 'lucide-react';
import { useBidVerification } from './hooks/useBidVerification';

const VerifyBid = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const inviteToken = searchParams.get('inviteToken');

  const {
    loading,
    status,
    intent,
    property,
    message,
    antiSnipingTriggered,
    newDeadlineTime,
    handleBackToProperty,
    navigateToProperty,
  } = useBidVerification(token, inviteToken);

  // Estado: Cargando
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <div className="bg-slate-800 rounded-2xl p-8 max-w-md w-full border border-slate-700 text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-amber-500 border-t-transparent mx-auto mb-4"></div>
          <p className="text-slate-300 text-lg">{t('verify.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="bg-slate-800 rounded-2xl p-8 max-w-md w-full border border-slate-700">
        
        {/* SUCCESS */}
        {status === 'success' && (
          <div className="text-center">
            <div className="w-20 h-20 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-12 h-12 text-emerald-400" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-4">
              {t('verify.success.title')}
            </h1>
            <p className="text-slate-300 mb-6">
              {message}
            </p>
            {property && (
              <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-4 mb-6">
                <p className="text-emerald-200 text-sm">
                  {property.title}
                </p>
              </div>
            )}
            {/* Badge anti-sniping */}
            {antiSnipingTriggered && (
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 mb-6">
                <p className="text-amber-300 text-sm font-medium">
                  {t('verify.antiSniping.title')}
                </p>
                <p className="text-amber-200/80 text-xs mt-1">
                  {t('verify.antiSniping.desc', { time: newDeadlineTime })}
                </p>
              </div>
            )}
            <button
              onClick={navigateToProperty}
              className="w-full bg-gradient-to-r from-amber-500 to-amber-600 text-slate-900 font-semibold py-3 px-6 rounded-lg hover:from-amber-600 hover:to-amber-700 transition-all"
            >
              {t('verify.backToProperty')}
            </button>
          </div>
        )}

        {/* EXPIRED */}
        {status === 'expired' && (
          <div className="text-center">
            <div className="w-20 h-20 bg-orange-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Clock className="w-12 h-12 text-orange-400" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-4">
              {t('verify.expired.title')}
            </h1>
            <p className="text-slate-300 mb-6">
              {message}
            </p>
            <button
              onClick={handleBackToProperty}
              className="w-full bg-gradient-to-r from-amber-500 to-amber-600 text-slate-900 font-semibold py-3 rounded-lg hover:from-amber-600 hover:to-amber-700 transition-all"
            >
              {t('verify.backToProperty')}
            </button>
          </div>
        )}

        {/* INVALID */}
        {status === 'invalid' && (
          <div className="text-center">
            <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertTriangle className="w-12 h-12 text-red-400" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-4">
              {t('verify.invalid.title')}
            </h1>
            <p className="text-slate-300 mb-6">
              {message}
            </p>
            <button
              onClick={handleBackToProperty}
              className="w-full bg-gradient-to-r from-amber-500 to-amber-600 text-slate-900 font-semibold py-3 rounded-lg hover:from-amber-600 hover:to-amber-700 transition-all"
            >
              {t('verify.makeNewBid')}
            </button>
          </div>
        )}

        {/* ERROR */}
        {status === 'error' && (
          <div className="text-center">
            <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-12 h-12 text-red-400" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-4">
              {t('verify.error.title')}
            </h1>
            <p className="text-slate-300 mb-6">
              {message}
            </p>
            <button
              onClick={handleBackToProperty}
              className="w-full bg-slate-700 text-white font-medium py-3 rounded-lg hover:bg-slate-600 transition-all"
            >
              {t('verify.backToProperty')}
            </button>
          </div>
        )}

      </div>
    </div>
  );
};

export default VerifyBid;